import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type CollectionAdminItem,
  type HomeBlock,
  type HomeLayoutAdminItem,
} from '@bailafm/shared';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { HomeBlocksEditor, hasEmptyCollectionBlocks } from '@/components/home/HomeBlocksEditor';
import { ApiClientError, collectionsApi, homeLayoutApi } from '@/lib/api';

export default function DestacadosPage() {
  const [layout, setLayout] = useState<HomeLayoutAdminItem | null>(null);
  const [collections, setCollections] = useState<CollectionAdminItem[]>([]);
  const [draft, setDraft] = useState<HomeBlock[]>([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [layoutData, collectionsData] = await Promise.all([
        homeLayoutApi.get(),
        collectionsApi.listAll(),
      ]);
      setLayout(layoutData);
      setCollections(collectionsData);
      setDraft(layoutData.blocks ?? []);
    } catch (err) {
      setLoadError(
        err instanceof ApiClientError ? err.message : 'No se pudo cargar el layout.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Detectar cambios: comparación simple de JSON (los ids persisten)
  const dirty = useMemo(
    () => JSON.stringify(layout?.blocks ?? []) !== JSON.stringify(draft),
    [layout, draft],
  );

  // Algún bloque de colección tiene collectionId vacío
  const hasEmptyCollection = useMemo(() => hasEmptyCollectionBlocks(draft), [draft]);

  async function handleSave() {
    if (!dirty || !layout) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await homeLayoutApi.update(layout._etag, {
        blocks: draft,
      });
      setLayout(updated);
      setDraft(updated.blocks);
      toast.success('Cambios guardados');
    } catch (err) {
      const message = formatSaveError(err);
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight">Destacados</h1>
          <p className="text-muted-foreground">
            Gestiona los bloques de la home: añade, reordena, activa o desactiva
            bloques de colecciones y secciones fijas de la página principal.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => void handleSave()}
          disabled={!dirty || saving || hasEmptyCollection}
        >
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </header>

      {saveError && (
        <p className="flex items-center gap-1 text-sm text-destructive">
          <AlertCircle className="size-4" aria-hidden /> {saveError}
        </p>
      )}

      {loadError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {loadError}
        </p>
      )}

      {loading ? (
        <p className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
          Cargando…
        </p>
      ) : (
        <>
          <section>
            <HomeBlocksEditor
              blocks={draft}
              collections={collections}
              onChange={setDraft}
            />
          </section>
        </>
      )}
    </div>
  );
}

function formatSaveError(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.code === 'etag_mismatch') {
      return 'Otra persona modificó el layout. Recarga la página y vuelve a intentarlo.';
    }
    if (err.code === 'home_collection_incomplete') {
      return 'Algunas colecciones elegidas no tienen los tres idiomas. Recarga la lista.';
    }
    return err.message;
  }
  return 'No se pudo guardar el layout.';
}
